<script setup lang="ts">
import { computed, ref } from 'vue';
import { formatUtcTimestamp } from '@/util/datetime';
import CopyButton from './CopyButton.vue';

const props = defineProps<{
  caseId: string;
  accountId?: string;
  externalId: string;
  externalCheckId?: string;
  createdAt: string;
  lastSavedAt?: string | null;
  lastReviewedBy?: string | null;
  completedAt?: string | null;
  supersedingAmlReviewId?: string;
}>();

const idRows = computed(() =>
  [
    { label: 'Case ID', value: props.caseId },
    { label: 'Cardholder ID', value: props.accountId },
    { label: 'External ID', value: props.externalId },
    { label: 'External check ID', value: props.externalCheckId },
  ].filter((row): row is { label: string; value: string } => Boolean(row.value))
);

const copiedAll = ref(false);
let copiedAllTimer: ReturnType<typeof setTimeout> | undefined;
async function copyAll() {
  await navigator.clipboard.writeText(
    idRows.value.map((row) => `${row.label}: ${row.value}`).join('\n')
  );
  copiedAll.value = true;
  clearTimeout(copiedAllTimer);
  copiedAllTimer = setTimeout(() => {
    copiedAll.value = false;
  }, 1500);
}

const lifecycleRows = computed(() => [
  { label: 'Opened', value: formatUtcTimestamp(props.createdAt), warn: false },
  {
    label: 'Last saved',
    value: props.lastSavedAt ? formatUtcTimestamp(props.lastSavedAt) : '—',
    warn: false,
  },
  { label: 'Last reviewed by', value: props.lastReviewedBy ?? '—', warn: false },
  {
    label: 'Completed at',
    value: props.completedAt ? formatUtcTimestamp(props.completedAt) : '—',
    warn: false,
  },
  {
    label: 'Superseded by',
    value: props.supersedingAmlReviewId ?? '—',
    warn: Boolean(props.supersedingAmlReviewId),
  },
]);
</script>

<template>
  <div data-test="case-ids-panel">
    <div
      v-for="row in idRows"
      :key="row.label"
      :data-test="`case-id-row-${row.label.toLowerCase().split(' ').join('-')}`"
      class="flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2"
    >
      <span class="w-24 flex-none text-[10px] font-semibold tracking-wide text-gray-400 uppercase">
        {{ row.label }}
      </span>
      <span class="min-w-0 flex-1 font-mono text-xs break-all text-gray-900">{{ row.value }}</span>
      <CopyButton :value="row.value" :label="row.label" />
    </div>

    <div class="flex flex-col gap-1 px-3 py-2.5">
      <span class="text-[10px] font-semibold tracking-wide text-gray-400 uppercase">
        This review
      </span>
      <div
        v-for="row in lifecycleRows"
        :key="row.label"
        data-test="case-lifecycle-row"
        class="flex items-baseline gap-2"
      >
        <span class="w-24 flex-none text-[11.5px] text-gray-500">{{ row.label }}</span>
        <span
          class="min-w-0 text-[11.5px] break-words"
          :class="row.warn ? 'font-semibold text-amber-700' : 'text-gray-900'"
          >{{ row.value }}</span
        >
      </div>
    </div>

    <div class="flex justify-end border-t border-gray-100 px-3 py-2">
      <button
        type="button"
        data-test="btn-copy-all-ids"
        class="text-[11px] font-medium text-indigo-600 hover:text-indigo-700"
        @click="copyAll"
      >
        {{ copiedAll ? 'Copied' : `Copy all ${idRows.length} IDs` }}
      </button>
    </div>
  </div>
</template>
